import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { FareFeaturePaymentType } from '@websky/graphql';
import { useTheme } from '../../../../../../../theme';
import { FareGroupOption, FareGroupVariant } from '@entities/fare';
import { WithTooltip } from '@shared/ui';

export interface Props extends FareGroupOption {
	className?: string;
	variant: FareGroupVariant;
}

const GridOption: React.FC<Props> = ({
	className,
	variant,
	optionIcon,
	paymentIcon,
	paymentType,
	optionType,
	title,
	tooltip
}) => {
	const { GridOption: css } = useTheme('Entities').FareGroupV2;

	const [tooltipOpen, setTooltipOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const handleTouchStart = useCallback(() => {
		setTooltipOpen(true);
	}, []);

	useEffect(() => {
		if (!tooltipOpen) {
			return undefined;
		}

		const handleOutsideTouch = (e: TouchEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setTooltipOpen(false);
			}
		};

		document.addEventListener('touchstart', handleOutsideTouch);

		return () => document.removeEventListener('touchstart', handleOutsideTouch);
	}, [tooltipOpen]);

	const showTitle = Boolean(paymentType === FareFeaturePaymentType.Free && title);

	const paymentIconElement = <div className={css.paymentIcon}>{paymentIcon}</div>;
	const titleElement = <div className={css.title}>{title}</div>;

	return (
		<WithTooltip
			title={tooltip}
			open={tooltip ? tooltipOpen : false}
			onOpen={() => setTooltipOpen(true)}
			onClose={() => setTooltipOpen(false)}
			disableTouchListener
		>
			<div
				data-option-type={optionType}
				className={cn(css.root, css[variant], className, {
					[css.pay]: paymentType === FareFeaturePaymentType.Pay,
					[css.free]: paymentType === FareFeaturePaymentType.Free,
					[css.notAvailable]: paymentType === FareFeaturePaymentType.NotAvailable,
					[css.withTooltip]: !!tooltip
				})}
				ref={containerRef}
				onTouchStart={handleTouchStart}
			>
				<div className={css.optionIcon}>{optionIcon}</div>

				{variant === 'small' && <>{showTitle ? titleElement : paymentIconElement}</>}

				{variant === 'big' && (
					<>
						{paymentIconElement}
						{titleElement}
					</>
				)}
			</div>
		</WithTooltip>
	);
};

export default GridOption;
